import { useEffect, useState } from "react";
import { Clock, Plus, Pencil, Trash2, CheckCircle2, FileEdit, User as UserIcon } from "lucide-react";
import { listHistory } from "../lib/api";
import { toast } from "sonner";

const ACTION_META = {
  create: { label: "Criou", icon: Plus, cls: "text-green-600 bg-green-50" },
  update: { label: "Atualizou", icon: Pencil, cls: "text-blue-600 bg-blue-50" },
  delete: { label: "Excluiu", icon: Trash2, cls: "text-red-600 bg-red-50" },
  complete: { label: "Concluiu", icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
};

const ENTITY_LABEL = {
  client: "Cliente",
  task: "Tarefa",
  user: "Usuário",
  report: "Ficha de processo",
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listHistory(300)
      .then(setItems)
      .catch(() => toast.error("Erro ao carregar histórico"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="history-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
          Histórico
        </h1>
        <p className="text-sm sm:text-base text-slate-500 mt-1">
          Registro automático de ações importantes no sistema.
        </p>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-history">
            <Clock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Nenhum evento registrado ainda.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="history-list">
            {items.map((it) => {
              const meta = ACTION_META[it.action] || { label: it.action, icon: FileEdit, cls: "text-slate-500 bg-slate-50" };
              const Icon = meta.icon;
              return (
                <li key={it.id} className="p-4 flex gap-3 items-start hover:bg-slate-50 transition-colors" data-testid={`history-item-${it.id}`}>
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.cls}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#0F172A]">
                      <span className="font-medium">{it.user_name || "—"}</span>{" "}
                      <span className="text-slate-500">{meta.label.toLowerCase()}</span>{" "}
                      <span className="text-slate-500">
                        {ENTITY_LABEL[it.entity_type] || it.entity_type}
                      </span>
                      {it.entity_name && (
                        <span className="font-medium text-[#0F2942]">: {it.entity_name}</span>
                      )}
                    </p>
                    {it.details && (
                      <p className="text-xs text-slate-400 mt-0.5">{it.details}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDateTime(it.timestamp)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
